if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        const linkedParams = new URLSearchParams(window.location.search);
        this.rfLinkedPurchaseView ??= linkedParams.has('variant') || linkedParams.has('option_values');
        if (!this.rfSummaryClickHandler) {
          this.rfSummaryClickHandler = event => {
            const keep = event.target.closest('[data-rf-pair-continue]');
            if (keep && this.contains(keep)) {
              event.preventDefault();
              event.stopPropagation();
              const disclosure = keep.closest('.rf-pair-disclosure');
              if (disclosure) disclosure.open = false;
              const groups = this.rfOrderedGroups();
              const next = groups.findIndex((group, index) => !this.rfChosenOptions?.has(index));
              const starter = keep.closest('.rf-taffy-pair-inline') && !groups.some(group => group.querySelector('input:checked'))
                && this.rfWidthStarterMode === 'width'
                ? this.variantSelectors?.querySelector('[data-rf-width-starter-ui]') : null;
              const target = starter && !starter.hidden ? starter : next >= 0 ? groups[next] : this.querySelector('.rf-price-qty-row, .rf-twill-price-qty, .rf-price-sample-row') || this.querySelector('[id^="ProductSubmitButton-"]');
              this.rfScrollChoice(target, true, false, true);
              return;
            }
            const button = event.target.closest('.rf-selection-summary button[data-rf-choice-index]');
            if (button && this.contains(button)) {
              event.preventDefault();
              event.stopPropagation();
              const groups = this.rfOrderedGroups();
              const group = groups[Number(button.dataset.rfChoiceIndex)];
              const colorIndex = groups.findIndex(item => item.dataset.rfOptionName?.trim().toLowerCase() === 'color');
              const starter = this.variantSelectors?.querySelector('[data-rf-width-starter-ui]');
              // Width-first uses the starter until Color is chosen; the ordinary Width group is hidden then.
              if (group?.dataset.rfDimension === 'width' && starter && this.rfWidthStarterMode === 'width'
                && this.rfWidthStarterWidth && !this.rfChosenOptions?.has(colorIndex)) {
                this.rfWidthStarterEditing = true;
                this.rfUpdateWidthStarter(groups);
                this.rfScrollChoice(starter.querySelector('[data-rf-starter-choices]'), true);
                return;
              }
              this.rfScrollChoice(group, true, true);
            }
          };
          this.addEventListener('click', this.rfSummaryClickHandler, true);
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        queueMicrotask(() => this.rfApplyChoices());
        customElements.whenDefined('variant-selects').then(() => {
          this.rfApplyChoices();
          this.rfQueueSecondsLanding();
          this.rfQueueVariantLanding();
        });
        this.addEventListener('submit', (event) => {
          if (event.target.matches('form[action*="/cart/add"]') && this.rfOrderedEnabled()
            && (this.dataset.rfChoiceComplete !== 'true' || this.dataset.rfChoicesBusy === 'true')) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.rfApplyChoices();
          }
        }, true);
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.rfShippingObserver?.disconnect();
        document.body.classList.remove('rf-mobile-shipping-focus-visible');
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      rfOrderedGroups(root = this) {
        return Array.from(root.querySelectorAll('variant-selects .product-form__input--pill'));
      }

      rfOrderedEnabled() {
        return this.id.startsWith('MainProduct-') && this.variantSelectors?.dataset.rfOrdered === 'true'
          && this.rfOrderedGroups().length > 0;
      }

      rfTwillVariants() {
        const source = this.variantSelectors?.querySelector('[data-rf-twill-variants]');
        if (!source) return null;
        return this.rfTwillVariantData ||= JSON.parse(source.textContent);
      }

      rfApplyTwillAvailability(input, group, index, groups) {
        const variants = this.rfTwillVariants();
        // Earlier choices constrain the next step. A width retained while
        // browsing other weights also constrains the weight choices.
        const matches = variants.filter(variant => variant.options[index] === input.value
          && groups.every((other, n) => n === index || !this.rfChosenOptions.has(n)
            || !(n < index || index === 0 && n === 1 && this.rfLinkedChoices?.has(n))
            || variant.options[n] === Array.from(other.querySelectorAll('input')).find(item => item.dataset.optionValueId === this.rfChosenOptions.get(n))?.value));
        const state = !matches.length ? 'not-offered' : matches.some(variant => variant.available) ? 'available' : 'sold-out';
        input.disabled = false;
        input.dataset.rfVariantState = state;
        input.dataset.rfSubtreeAvailable = String(matches.some(variant => variant.available));
        input.classList.toggle('disabled', state !== 'available');
        const label = input.nextElementSibling;
        if (!label) return;
        label.classList.remove('rf-width-absent');
        for (const value of ['available', 'sold-out', 'not-offered']) label.classList.toggle('rf-dimension-option--' + value, state === value);
        let note = label.querySelector('.rf-dimension-option__status');
        const text = state === 'sold-out' ? 'Out of stock' : state === 'not-offered'
          ? (group.dataset.rfDimension === 'width' ? 'See other weights →' : 'See other options →') : '';
        if (!text) { note?.remove(); return; }
        if (!note) { note = document.createElement('span'); label.append(note); }
        note.className = 'rf-dimension-option__status' + (state === 'not-offered' ? ' rf-dimension-option__status--not-offered' : '');
        if (note.textContent !== text) note.textContent = text;
      }

      rfPullOpen() {
        return this.variantSelectors?.dataset.rfPullOpen === 'true';
      }

      rfPullVariants() {
        return JSON.parse(this.variantSelectors.querySelector('[data-rf-pull-variants]')?.textContent || '[]');
      }

      rfApplyPullAvailability(input, group, index, groups) {
        const variants = this.rfPullVariants();
        const matches = variants.filter((variant) => variant.options[index] === input.value
          && (index === 0 || groups.every((other, n) => n === index || !this.rfChosenOptions.has(n)
            || variant.options[n] === other.querySelector('input:checked')?.value)));
        const state = !matches.length ? 'not-offered' : matches.some((variant) => variant.available) ? 'available' : 'sold-out';
        input.disabled = false;
        input.dataset.rfVariantState = state;
        input.dataset.rfSubtreeAvailable = String(matches.some((variant) => variant.available));
        input.classList.toggle('disabled', state !== 'available');
        const label = input.nextElementSibling;
        if (!label?.classList.contains('rf-dimension-option')) return;
        for (const value of ['available', 'sold-out', 'not-offered']) label.classList.toggle('rf-dimension-option--' + value, state === value);
        let note = label.querySelector('.rf-dimension-option__status');
        const text = state === 'sold-out' ? 'Out of stock' : state === 'not-offered' ? 'See other options →' : '';
        if (!text) { note?.remove(); return; }
        if (!note) { note = document.createElement('span'); label.append(note); }
        note.className = 'rf-dimension-option__status' + (state === 'not-offered' ? ' rf-dimension-option__status--not-offered' : '');
        if (note.textContent !== text) note.textContent = text;
      }

      rfInitialChoices() {
        const chosen = new Map();
        this.rfLinkedChoices = new Set();
        // Entering the Seconds catalog starts with every in-stock Seconds
        // color visible, regardless of the variant behind the card or link.
        if (window.rfSecondsEntry) return chosen;
        const params = new URLSearchParams(window.location.search);
        const variantId = params.get('variant');
        const variant = this.getSelectedVariant(this);
        const ids = new Set((params.get('option_values') || '').split(',').filter(Boolean));
        this.rfOrderedGroups().forEach((group, index) => {
          const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) =>
            variantId && String(variant?.id) === variantId ? input.value === variant.options[index] : ids.has(input.dataset.optionValueId));
          if (input) {
            chosen.set(index, input.dataset.optionValueId);
            this.rfLinkedChoices.add(index);
          }
        });
        if (this.rfPullOpen() && !variantId && !ids.size) {
          const standard = this.rfOrderedGroups()[0]?.querySelector('input[value="Standard"], input[value="Seconds"]');
          if (standard) chosen.set(0, standard.dataset.optionValueId);
        }
        return chosen;
      }

      rfScrollChoice(group, force = false, revealLinked = false, skipOffer = false) {
        if (!group?.isConnected) return;
        if (this.rfLinkedPurchaseView && revealLinked) {
          const index = this.rfOrderedGroups().indexOf(group);
          this.rfLinkedEditIndex = this.rfLinkedEditIndex === index ? undefined : index;
          this.rfApplyChoices();
        }
        if (group.dataset.rfLinkedChoiceCollapsed === 'true') {
          group = this.variantSelectors?.querySelector('.rf-selection-summary:not([hidden])') || group;
        }
        const header = document.querySelector('sticky-header')?.getBoundingClientRect();
        const summary = this.variantSelectors?.querySelector('.rf-selection-summary');
        // Refresh the sticky offset at the same time as the actual scroll,
        // rather than retaining a header size captured before the render.
        if (summary) summary.style.setProperty('--rf-summary-top', Math.round(header?.height || 72) + 'px');
        const stickySummary = summary && !summary.hidden && getComputedStyle(summary).position === 'sticky';
        // The summary is constrained by the selector's bottom edge. Preserve
        // enough clearance for an outside purchase row to keep it below the header.
        const clearance = skipOffer && stickySummary && !this.variantSelectors.contains(group)
          ? Math.max(16, group.getBoundingClientRect().top - this.variantSelectors.getBoundingClientRect().bottom
            + (parseFloat(getComputedStyle(summary).marginBottom) || 0) + 4) : 16;
        // The first choice can be clicked before the header reaches the top.
        // Reserve its height because the destination will make it sticky.
        let offset = (header?.height || 0)
          + (stickySummary ? summary.getBoundingClientRect().height : 0) + clearance;
        const secondsOffer = this.variantSelectors?.querySelector('.rf-seconds-offer:not([hidden])');
        const pair = this.variantSelectors?.querySelector('.rf-pair-inline, .rf-taffy-pair-inline');
        const pairVisible = pair && pair.getBoundingClientRect().height > 0 && getComputedStyle(pair).display !== 'none';
        let anchor = !skipOffer && pairVisible && pair.nextElementSibling === group ? pair : !skipOffer && secondsOffer?.nextElementSibling === group ? secondsOffer : group;
        const bars = Array.from(document.querySelectorAll('#rf-desktop-purchase-bar, [id^="rf-mobile-sticky-"]'))
          .map(bar => bar.getBoundingClientRect()).filter(rect => rect.height > 0 && rect.top > 0 && rect.top < window.innerHeight);
        const viewportBottom = Math.min(window.innerHeight - 24, ...bars.map(rect => rect.top - 16));
        if (skipOffer && stickySummary) {
          const actions = Array.from(this.querySelectorAll('[id^="ProductSubmitButton-"], [id^="hcpCta-"]'))
            .map(action => action.getBoundingClientRect()).filter(rect => rect.height > 0);
          const span = Math.max(group.getBoundingClientRect().bottom, ...actions.map(rect => rect.bottom)) - group.getBoundingClientRect().top;
          // Long expanded terms cannot share a viewport with the full summary.
          // Finish at the purchase controls rather than placing them below the screen.
          if (offset + span > viewportBottom) offset = (header?.height || 0) + 16;
        }
        if (skipOffer && summary && !summary.hidden && !stickySummary) {
          const actions = Array.from(this.querySelectorAll('[id^="ProductSubmitButton-"], [id^="hcpCta-"]'))
            .map(action => action.getBoundingClientRect()).filter(rect => rect.height > 0);
          const end = Math.max(group.getBoundingClientRect().bottom, ...actions.map(rect => rect.bottom));
          if (end - summary.getBoundingClientRect().top <= viewportBottom - offset) anchor = summary;
        }
        if (anchor !== group && anchor !== summary && group.getBoundingClientRect().bottom - anchor.getBoundingClientRect().top > viewportBottom - offset) anchor = group;
        const rect = anchor.getBoundingClientRect();
        if (force || rect.top < offset || rect.bottom > window.innerHeight - 24) {
          window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - offset), behavior: 'instant' });
        }
      }

      rfQueueSecondsLanding() {
        if (!window.rfSecondsEntry || !this.id.startsWith('MainProduct-') || this.rfSecondsLandingQueued) return;
        this.rfSecondsLandingQueued = true;
        const align = () => requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!this.isConnected || this.rfSecondsLandingCancelled || this.rfChosenOptions?.size) return;
          const prompt = this.variantSelectors?.querySelector('.rf-order-prompt:not([hidden])');
          if (!prompt) return;
          const header = document.querySelector('sticky-header')?.getBoundingClientRect();
          const offset = (header?.height || 0) + 12;
          const difference = prompt.getBoundingClientRect().top - offset;
          if (Math.abs(difference) > 4) {
            window.scrollTo({ top: Math.max(0, window.scrollY + difference), behavior: 'instant' });
          }
        }));
        const land = () => {
          align();
          // Header and product media can settle after load on slower devices.
          [150, 350, 800].forEach((delay) => window.setTimeout(align, delay));
        };
        const cancel = () => { this.rfSecondsLandingCancelled = true; };
        window.addEventListener('pointerdown', cancel, { once: true, passive: true });
        window.addEventListener('wheel', cancel, { once: true, passive: true });
        window.addEventListener('touchstart', cancel, { once: true, passive: true });
        window.addEventListener('keydown', cancel, { once: true, passive: true });
        if (document.readyState === 'complete') land();
        else window.addEventListener('load', land, { once: true });
      }

      rfQueueVariantLanding() {
        if (!this.rfLinkedPurchaseView || window.rfSecondsEntry || !this.id.startsWith('MainProduct-') || this.rfVariantLandingQueued) return;
        const params = new URLSearchParams(window.location.search);
        if (!params.has('variant') && !params.has('option_values')) return;
        const information = this.querySelector('[id^="ProductInfo-"]');
        if (information?.querySelector('[data-rf-layout-gate]') && !information.classList.contains('rf-layout-ready')) return;
        if (!this.rfVariantLandingWatching) {
          this.rfVariantLandingWatching = true;
          const cancel = () => { this.rfVariantLandingCancelled = true; };
          window.addEventListener('pointerdown', cancel, { once:true, passive:true });
          window.addEventListener('wheel', cancel, { once:true, passive:true });
          window.addEventListener('touchstart', cancel, { once:true, passive:true });
          window.addEventListener('keydown', cancel, { once:true, passive:true });
        }
        const waitsForFacts = /awareness-ribbons/.test(this.dataset.url || '');
        if (waitsForFacts && this.dataset.rfTopCardsArranged !== 'true') return;
        const summaryReady = this.variantSelectors?.querySelector('.rf-selection-summary:not([hidden])') && this.rfChosenOptions?.size;
        const selectedId = this.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
        const singleReady = !this.rfOrderedEnabled() && params.get('variant') === String(selectedId);
        if (!summaryReady && !singleReady) return;
        this.rfVariantLandingQueued = true;
        const align = (settled = false) => {
          if (!this.isConnected || this.rfVariantLandingCancelled) return;
          const summary = this.variantSelectors?.querySelector('.rf-selection-summary:not([hidden])');
          const selectedId = this.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
          const single = !this.rfOrderedEnabled() && params.get('variant') === String(selectedId)
            ? this.querySelector('.product__title') : null;
          const target = summary || single;
          if (!target || (summary && !this.rfChosenOptions?.size)) return;
          const header = document.querySelector('sticky-header')?.getBoundingClientRect();
          const offset = (header?.height || 0) + 12;
          const difference = target.getBoundingClientRect().top - offset;
          if (Math.abs(difference) > 4) {
            window.scrollTo({ top: Math.max(0, window.scrollY + difference), behavior: 'instant' });
          }
          if (settled) this.dataset.rfLinkedLandingSettled = 'true';
        };
        const land = () => { const positioned = () => requestAnimationFrame(() => align(true)); if (document.fonts?.ready) document.fonts.ready.then(positioned); else positioned(); };
        if (document.readyState !== 'complete') window.addEventListener('load', land, { once: true });
        else land();
      }

      rfWatchShippingChat() {
        const shipping = this.querySelector('.rf-shipping-offer');
        if (!shipping || this.rfShippingTarget === shipping) return;
        this.rfShippingObserver?.disconnect();
        this.rfShippingTarget = shipping;
        this.rfShippingObserver = new IntersectionObserver(([entry]) => {
          document.body.classList.toggle('rf-mobile-shipping-focus-visible',
            entry.isIntersecting && window.matchMedia('(max-width: 749px)').matches
            && this.isConnected && this.dataset.rfChoiceComplete === 'true'
            && this.rfOrderedGroups().at(-1)?.dataset.rfDimension === 'length');
        }, { threshold: 0.25 });
        this.rfShippingObserver.observe(shipping);
      }

      rfWidthPreviewPhoto(colorValue, groups = this.rfOrderedGroups()) {
        const source = this.variantSelectors?.querySelector('[data-rf-width-photo-previews]');
        if (!source) return null;
        if (this.rfWidthPreviewSource !== source.textContent) {
          this.rfWidthPreviewSource = source.textContent;
          try { this.rfWidthPreviewMap = JSON.parse(source.textContent); }
          catch { this.rfWidthPreviewMap = null; }
        }
        const selected = (group, index) => Array.from(group?.querySelectorAll('input[type="radio"]') || [])
          .find(input => input.dataset.optionValueId === this.rfChosenOptions?.get(index));
        const widthIndex = groups.findIndex(group => group.dataset.rfOptionName?.trim().toLowerCase() === 'width');
        const colorIndex = groups.findIndex(group => group.dataset.rfOptionName?.trim().toLowerCase() === 'color');
        const color = colorValue ?? selected(groups[colorIndex], colorIndex)?.value;
        const entry = this.rfWidthPreviewMap?.colors?.[color] || this.rfWidthPreviewMap?.default;
        if (!entry) return null;
        const widthHasChoices = groups[widthIndex]?.querySelectorAll('input[type="radio"]').length > 1;
        if (widthIndex >= 0 && this.rfChosenOptions?.has(widthIndex)
          && (widthHasChoices || this.rfLinkedChoices?.has(widthIndex))) {
          const width = selected(groups[widthIndex], widthIndex)?.value;
          if (entry.widths?.[width]) return entry.widths[width];
          if (colorIndex < 0 && !this.getSelectedVariant(this)?.featured_media?.id) return entry.wide;
          const colorInput = Array.from(groups[colorIndex]?.querySelectorAll('input[type="radio"]') || []).find(input => input.value === color);
          const nativePhoto = colorInput?.nextElementSibling?.querySelector('img')?.dataset.rfNativeColorPhoto;
          const asset = url => url?.split('?')[0].split('/').at(-1);
          return nativePhoto && Object.values(entry.widths || {}).some(photo => asset(photo.url) === asset(nativePhoto)) ? entry.wide : null;
        }
        return entry.wide || null;
      }

      rfSyncWidthPhotoPreviews(groups) {
        if (!this.variantSelectors?.querySelector('[data-rf-width-photo-previews]')) return;
        const colorGroup = groups.find(group => group.dataset.rfOptionName?.trim().toLowerCase() === 'color');
        colorGroup?.querySelectorAll('input[type="radio"]').forEach(input => {
          const image = input.nextElementSibling?.querySelector('img[data-rf-native-color-photo]');
          if (!image) return;
          const photo = this.rfWidthPreviewPhoto(input.value, groups);
          const source = photo?.url || image.dataset.rfNativeColorPhoto;
          if (source && image.getAttribute('src') !== source) image.src = source;
        });
        const preferred = this.rfWidthPreviewPhoto(undefined, groups);
        const gallery = this.querySelector('media-gallery');
        const mediaId = preferred && this.dataset.section + '-' + preferred.mediaId;
        if (mediaId && gallery?.querySelector('[data-media-id="' + mediaId + '"]:not(.is-active)')) {
          gallery.setActiveMedia?.(mediaId, false, { scrollPage: false });
        }
      }

      rfUpdateChoiceSummary(groups, next) {
        let summary = this.variantSelectors.querySelector('.rf-selection-summary');
        if (!summary) {
          summary = document.createElement('nav');
          summary.className = 'rf-selection-summary';
          summary.setAttribute('aria-label', 'Your product selections');
          this.variantSelectors.prepend(summary);
          // The product-info owner delegates summary clicks so layout clones remain editable.
        }
        const chosen = groups.map((group, index) => {
          const input = group.querySelector('input:checked');
          let name = group.dataset.rfOptionName?.trim() || 'Option';
          if (this.rfPullOpen()) name = ['Type', 'Strength', 'Length'][index] || name;
          if (name.toLowerCase() === 'number of pre-formed ribbons') name = 'Pack';
          if (['/products/white-twill-tape','/products/natural-twill-tape','/products/pat-95-medium-weight-black-cotton-twill'].includes(this.dataset.url) && /^(model|style|weight)$/i.test(name)) name = 'Weight / Thickness';
          const controls = Array.from(group.querySelectorAll('input[type="radio"]'));
          const editable = controls.length > 1 && (!group.dataset.rfDimension || controls.some(control => control.nextElementSibling && getComputedStyle(control.nextElementSibling).display !== 'none'));
          return input ? { index, name, value: input.dataset.rfChoiceLabel || input.value, editable } : null;
        }).filter(Boolean);
        const signature = JSON.stringify([chosen, next]);
        if (summary.dataset.rfChoices !== signature) {
          summary.dataset.rfChoices = signature;
          summary.replaceChildren();
          for (const choice of chosen) {
            const control = document.createElement(choice.editable ? 'button' : 'span');
            if (choice.editable) {
              const label = document.createElement('span');
              label.className = 'rf-selection-summary__control-label';
              label.textContent = 'Change ' + (choice.name.toLowerCase() === 'color' && this.dataset.url === '/products/specialty-stripes-ribbon' ? 'pattern' : choice.name.toLowerCase());
              const value = document.createElement('strong');
              value.className = 'rf-selection-summary__control-value';
              value.textContent = choice.value;
              control.append(label, value);
              control.type = 'button';
              control.dataset.rfChoiceIndex = choice.index;
              control.setAttribute('aria-label', 'Change ' + choice.name.toLowerCase() + ': ' + choice.value);
            } else {
              control.className = 'rf-selection-value';
              control.textContent = choice.name + ': ' + choice.value;
            }
            summary.append(control);
          }
          if (next >= 0) {
            const pending = document.createElement('span');
            pending.className = 'rf-selection-summary__next';
            pending.textContent = 'Choose ' + (groups[next].dataset.rfOptionName || 'option').trim().toLowerCase();
            summary.append(pending);
          }
        }
        summary.hidden = !chosen.length || (!this.rfLinkedPurchaseView && (groups.every((group) => group.querySelectorAll('input[type="radio"]').length === 1) || this.rfPullOpen() && next === 1 && chosen.length === 1));
        if (chosen.length) {
          summary.dataset.rfProductTitle = this.querySelector('.product__title h1')?.textContent.trim() || '';
          let heading = summary.querySelector('.rf-selection-summary__heading');
          if (!heading) {
            heading = document.createElement('div');
            heading.className = 'rf-selection-summary__heading';
            const image = document.createElement('img');
            image.width = 48;
            image.height = 48;
            image.decoding = 'async';
            const copy = document.createElement('div');
            const title = document.createElement('strong');
            const hint = document.createElement('small');
            hint.textContent = 'Change options below';
            copy.append(title, hint);
            heading.append(image, copy);
            summary.prepend(heading);
          }
          const title = heading.querySelector('strong');
          if (title.textContent !== summary.dataset.rfProductTitle) title.textContent = summary.dataset.rfProductTitle;
          const narrowPhoto = groups.some((group, index) => this.rfChosenOptions?.has(index) && group.dataset.rfOptionName?.trim().toLowerCase() === 'width' && /^3\s*\/\s*8/.test(group.querySelector('input:checked')?.value || '')) ? this.querySelector('media-gallery .is-active img[src*="rf-narrow-38-"]') : null;
          const photo = narrowPhoto || groups.map(group => group.querySelector('input:checked')?.nextElementSibling?.querySelector('img')).find(Boolean)
            || this.querySelector('media-gallery .is-active img') || this.querySelector('media-gallery img');
          const image = heading.querySelector('img');
          const source = this.rfWidthPreviewPhoto(undefined, groups)?.url || photo?.currentSrc || photo?.src;
          image.hidden = !source;
          if (source && image.getAttribute('src') !== source) image.src = source;
          image.alt = photo?.alt || chosen.map(choice => choice.value).join(' · ');
        }
        if (this.rfLinkedPurchaseView) {
          summary.querySelectorAll('button[data-rf-choice-index]').forEach(button => {
            button.setAttribute('aria-expanded', String(this.rfLinkedEditIndex === Number(button.dataset.rfChoiceIndex)));
          });
        }
        const pair = this.variantSelectors.querySelector('.rf-pair-inline, .rf-taffy-pair-inline');
        const outTown = groups.some(group => group.dataset.rfOptionName?.trim().toLowerCase() === 'color' && group.querySelector('input:checked')?.value === 'Out on the Town');
        if (pair?.classList.contains('rf-taffy-pair-inline')) {
          pair.hidden = chosen.some(choice => choice.name.toLowerCase() === 'color');
        }
        if (pair && !pair.hidden && (outTown || pair.classList.contains('rf-taffy-pair-inline'))) {
          if (pair.classList.contains('rf-taffy-pair-inline')) {
            const keep = pair.querySelector('[data-rf-pair-continue]');
            const nextName = next >= 0 ? (groups[next].dataset.rfOptionName || 'option').trim().toLowerCase() : null;
            const text = !chosen.length ? 'or continue to choose a ' + (this.rfWidthStarterMode || 'color')
              : nextName ? 'or continue to choose a ' + nextName : 'or continue with your selection';
            if (keep && keep.textContent !== text) keep.textContent = text;
            if (keep && keep.getAttribute('aria-label') !== text) keep.setAttribute('aria-label', text);
          }
          const target = next >= 0 ? groups[next] : !this.rfLinkedPurchaseView ? groups.at(-1) : null;
          const starter = pair.classList.contains('rf-taffy-pair-inline')
            && target?.dataset.rfOptionName?.trim().toLowerCase() === 'color'
            ? this.variantSelectors.querySelector('[data-rf-width-starter-ui]') : null;
          if (starter && !starter.hidden && getComputedStyle(starter).display !== 'none') {
            if (starter.nextElementSibling !== pair) starter.after(pair);
          } else if (target && target.dataset.rfOptionName?.trim().toLowerCase() !== 'color') {
            if (pair.nextElementSibling !== target) target.before(pair);
          } else if (summary.nextElementSibling !== pair) summary.after(pair);
        }
        const height = document.querySelector('sticky-header')?.getBoundingClientRect().height || 72;
        const top = Math.round(height) + 'px';
        if (summary.style.getPropertyValue('--rf-summary-top') !== top) summary.style.setProperty('--rf-summary-top', top);
      }

      rfLoadSecondsStarterWidths(starter, groups) {
        if (!window.rfSecondsEntry || this.rfSecondsStarterWidthPromise || this.rfSecondsStarterWidths) return;
        const buttons = Array.from(starter.querySelectorAll('[data-rf-starter-width]'));
        const hint = starter.querySelector('.rf-width-starter__hint');
        const colorGroup = groups.find((group) => group.dataset.rfOptionName?.trim().toLowerCase() === 'color');
        const colors = Array.from(colorGroup?.querySelectorAll('input[type="radio"]') || [])
          .filter((input) => input.value.includes('"Seconds Quality"') && input.dataset.rfSubtreeAvailable === 'true');
        buttons.forEach((button) => { button.hidden = true; });
        if (!colors.length) {
          if (hint) hint.textContent = 'No Seconds widths are currently available.';
          return;
        }
        if (hint) {
          hint.setAttribute('role', 'status');
          hint.textContent = 'Checking available Seconds widths…';
        }
        const section = this.dataset.originalSection || this.dataset.section;
        this.rfSecondsStarterWidthPromise = Promise.all(colors.map((color) => {
          const request = new URL(this.dataset.url, window.location.origin);
          request.searchParams.set('section_id', section);
          request.searchParams.set('option_values', color.dataset.optionValueId);
          return fetch(request).then((response) => {
            if (!response.ok) throw new Error('Seconds widths unavailable');
            return response.text();
          }).then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const widths = Array.from(doc.querySelectorAll('fieldset[data-rf-dimension="width"] input[type="radio"]'));
            if (!widths.length) throw new Error('Seconds widths missing');
            return widths.filter((input) => input.dataset.rfSubtreeAvailable === 'true').map((input) => input.value);
          });
        })).then((lists) => {
          if (!starter.isConnected) return;
          const available = new Set(lists.flat());
          this.rfSecondsStarterWidths = available;
          buttons.forEach((button) => { button.hidden = !available.has(button.dataset.rfStarterWidth); });
          if (hint) hint.textContent = available.size
            ? 'Choose an available Seconds width to see its colors.'
            : 'No Seconds widths are currently available.';
        }).catch(() => {
          this.rfSecondsStarterWidthPromise = null;
          if (hint) hint.textContent = 'Could not load Seconds widths. Choose Color or try Width again.';
        });
      }

      rfUpdateWidthStarter(groups) {
        const picker = this.variantSelectors;
        const starter = picker?.querySelector('[data-rf-width-starter-ui]');
        if (!starter) return;
        this.rfLoadSecondsStarterWidths(starter, groups);
        const widthIndex = groups.findIndex((group) => group.dataset.rfDimension === 'width');
        const colorIndex = groups.findIndex((group) => group.dataset.rfOptionName?.toLowerCase() === 'color');
        if (!this.rfWidthStarterWidth && this.rfChosenOptions?.has(widthIndex) && !this.rfChosenOptions.has(colorIndex)) {
          this.rfWidthStarterWidth = Array.from(groups[widthIndex].querySelectorAll('input'))
            .find((input) => input.dataset.optionValueId === this.rfChosenOptions.get(widthIndex))?.value;
          this.rfWidthStarterMode = 'width';
        }
        const switchHint = starter.querySelector('[data-rf-starter-switch-hint]');
        if (switchHint) switchHint.hidden = this.rfWidthStarterMode !== 'color';
        const mode = this.rfWidthStarterMode || 'color';
        const picked = mode === 'width' && !!this.rfWidthStarterWidth;
        picker.dataset.rfWidthStarterMode = mode;
        picker.dataset.rfWidthStarterPicked = String(picked);
        starter.querySelectorAll('[data-rf-starter-mode]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.rfStarterMode === mode));
        });
        const choices = starter.querySelector('[data-rf-starter-choices]');
        const chosen = starter.querySelector('[data-rf-starter-chosen]');
        if (choices && widthIndex >= 0) {
          const order = new Map(Array.from(groups[widthIndex].querySelectorAll('input[type="radio"]'))
            .map((input, index) => [input.value, index]));
          const buttons = Array.from(choices.querySelectorAll('[data-rf-starter-width]'));
          const sorted = [...buttons].sort((a, b) => (order.get(a.dataset.rfStarterWidth) ?? 999)
            - (order.get(b.dataset.rfStarterWidth) ?? 999));
          if (buttons.some((button, index) => button !== sorted[index])) sorted.forEach((button) => choices.append(button));
        }
        if (choices) choices.hidden = mode !== 'width' || (picked && !this.rfWidthStarterEditing);
        if (picked && !this.rfChosenOptions?.has(colorIndex)) {
          picker.querySelector('.rf-selection-summary button[data-rf-choice-index="' + widthIndex + '"]')
            ?.setAttribute('aria-expanded', String(!!this.rfWidthStarterEditing));
        }
        if (chosen) chosen.hidden = !picked || !!this.rfWidthStarterEditing;
        const selected = starter.querySelector('[data-rf-starter-selected-width]');
        if (selected && selected.textContent !== this.rfWidthStarterWidth) selected.textContent = this.rfWidthStarterWidth || '';
        starter.querySelectorAll('[data-rf-starter-width]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.rfStarterWidth === this.rfWidthStarterWidth));
        });
      }

      rfSyncPullCodePromos(groups, complete) {
        const panel = this.querySelector('[data-rf-pull-code-promos]');
        const source = panel?.querySelector('[data-rf-pull-code-map]');
        if (!panel || !source || !this.rfPullOpen()) return;
        if (this.rfPullCodePromoSource !== source.textContent) {
          try { this.rfPullCodePromoMap = JSON.parse(source.textContent); }
          catch { return; }
          this.rfPullCodePromoSource = source.textContent;
        }
        // Resolve current confirmed options, never the stale hidden variant ID
        // that can remain while the product section is being refreshed.
        const options = groups.map(group => group.querySelector('input:checked')?.value);
        const variant = complete ? this.rfPullVariants().find(item => item.options.length === options.length
          && item.options.every((value, index) => value === options[index])) : null;
        const id = variant ? String(variant.id) : '';
        const offer = complete ? ['pair', 'other'].find(kind => this.rfPullCodePromoMap[kind]?.includes(id)) : null;
        panel.hidden = !complete || !offer;
        panel.dataset.rfPullCodeOffer = offer || '';
        panel.querySelectorAll('[data-rf-pull-code-kind]').forEach(item => {
          item.hidden = !complete || item.dataset.rfPullCodeKind !== offer;
        });
      }

      rfApplyChoices() {
        if (!this.rfOrderedEnabled()) return;
        // The legacy sample initializer can mark the whole information column
        // as a purchase row at tablet/desktop widths without a buy wrapper.
        const info = this.querySelector('.product__info-container.rf-sample-after-qty');
        const price = info?.querySelector(':scope > .rf-price-sample-row');
        const quantity = info?.querySelector(':scope > .product-form__quantity');
        if (price && quantity) {
          const row = document.createElement('div');
          row.className = 'rf-price-qty-row rf-sample-after-qty';
          price.before(row);
          row.append(price, quantity);
          info.querySelectorAll(':scope > .rf-product-sample, :scope > .rf-sample-toggle').forEach((sample) => row.append(sample));
          info.classList.remove('rf-sample-after-qty');
        }
        this.rfChosenOptions ||= this.rfInitialChoices();
        const groups = this.rfOrderedGroups();
        const secondsFilter = window.rfSecondsPickerActive?.(this.variantSelectors) || false;
        this.variantSelectors.dataset.rfFilterSeconds = String(secondsFilter);
        groups.forEach((group, index) => {
          const inputs = group.querySelectorAll('input[type="radio"]');
          if (inputs.length === 1) this.rfChosenOptions.set(index, inputs[0].dataset.optionValueId);
        });
        this.rfUpdateWidthStarter(groups);
        const next = groups.findIndex((group, index) => !this.rfChosenOptions.has(index));
        const complete = next === -1;
        if (this.dataset.rfChoiceComplete !== String(complete)) this.dataset.rfChoiceComplete = String(complete);
        this.dataset.rfLinkedPurchaseView = String(Boolean(this.rfLinkedPurchaseView && this.rfChosenOptions.size));
        if (complete && groups.at(-1)?.dataset.rfDimension === 'length') this.rfWatchShippingChat();
        else document.body.classList.remove('rf-mobile-shipping-focus-visible');
        window.rfColorSelectionStarted = complete || groups.some((group, index) => group.dataset.rfDimension && this.rfLinkedChoices?.has(index) && this.rfChosenOptions.has(index));
        if (this.rfPullOpen()) groups.forEach((group, index) => group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = this.rfChosenOptions.get(index) === input.dataset.optionValueId;
        }));
        groups.forEach((group, index) => {
          const linkedChoice = this.rfLinkedPurchaseView && this.rfChosenOptions.has(index)
            && Array.from(group.querySelectorAll('input[type="radio"]')).some(input => input.dataset.optionValueId === this.rfChosenOptions.get(index));
          const linkedCollapsed = Boolean(linkedChoice && this.rfLinkedEditIndex !== index);
          group.dataset.rfLinkedChoiceCollapsed = String(linkedCollapsed);
          // This existing choice owner also owns display while collapsed. Some
          // color maps use product-specific grid rules that out-rank shared CSS.
          if (linkedCollapsed) group.style.setProperty('display', 'none', 'important');
          else if (group.dataset.rfCollapseDisplayOwned === 'true') group.style.removeProperty('display');
          group.dataset.rfCollapseDisplayOwned = String(linkedCollapsed);
          const locked = !this.rfPullOpen() && !complete && index > next;
          if (group.dataset.rfStepLocked !== String(locked)) group.dataset.rfStepLocked = String(locked);
          const widthLoading = !this.rfTwillVariants() && group.dataset.rfDimension === 'width' && !locked
            && groups.slice(0, index).some((prior) => prior.dataset.rfDimension !== 'length')
            && (!group.dataset.rfWidthMapReady || group.dataset.rfWidthMapReady !== group.dataset.rfWidthMapPending);
          const colorLoading = group.dataset.rfOptionName?.trim().toLowerCase() === 'color' && !this.rfChosenOptions.has(index)
            && window.rfColorSelectionStarted && (!group.dataset.rfColorMapReady || group.dataset.rfColorMapReady !== group.dataset.rfColorMapPending);
          const disabled = locked || widthLoading || colorLoading || !customElements.get('variant-selects') || this.dataset.rfChoicesBusy === 'true';
          if (group.disabled !== disabled) group.disabled = disabled;
          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = this.rfChosenOptions.get(index) === input.dataset.optionValueId;
            if (this.rfPullOpen()) { this.rfApplyPullAvailability(input, group, index, groups); return; }
            if (this.rfTwillVariants()) { this.rfApplyTwillAvailability(input, group, index, groups); return; }
            if (group.dataset.rfDimension === 'width' && !locked && !widthLoading) {
              const label = input.nextElementSibling;
              const absent = label?.classList.contains('rf-width-absent');
              const soldOut = !absent && input.dataset.rfSubtreeAvailable === 'false';
              input.disabled = false;
              label?.classList.toggle('rf-dimension-option--sold-out', soldOut);
              let note = label?.querySelector('.rf-ordered-stock');
              if (soldOut && !note) {
                note = document.createElement('span');
                note.className = 'rf-dimension-option__status rf-ordered-stock';
                note.textContent = 'Out of stock';
                label?.append(note);
              } else if (!soldOut) note?.remove();
              const absentNote = label?.querySelector('.rf-dimension-option__status--not-offered');
              const invitation = groups.some((item) => item.dataset.rfOptionName?.trim().toLowerCase() === 'color') ? 'See other colors →' : 'See other lengths →';
              if (absentNote && absentNote.textContent !== invitation) absentNote.textContent = invitation;
            } else if (group.querySelector('.rf-dimension-option') && !locked) {
              const availablePrefix = index < groups.length - 1 && input.dataset.rfSubtreeAvailable === 'true';
              const absent = input.dataset.rfVariantState === 'not-offered' && !availablePrefix;
              input.disabled = false;
              if (availablePrefix) {
                input.dataset.rfVariantState = 'available';
                input.classList.remove('disabled');
                input.nextElementSibling?.classList.remove('rf-dimension-option--not-offered', 'rf-dimension-option--sold-out');
                input.nextElementSibling?.querySelector('.rf-dimension-option__status')?.remove();
              }
              const note = input.nextElementSibling?.querySelector('.rf-dimension-option__status--not-offered');
              const text = groups.some((item) => item.dataset.rfOptionName?.trim().toLowerCase() === 'color') ? 'See other colors →' : 'See other options →';
              if (note && note.textContent !== text) note.textContent = text;
            }
            // Before the final step, stock is relative to the chosen prefix,
            // never to a server-default value in a later option group.
            if (!window.rfColorSelectionStarted && group.dataset.rfOptionName?.toLowerCase() === 'color') {
              const soldOut = input.dataset.rfSubtreeAvailable === 'false';
              const label = input.nextElementSibling;
              input.classList.toggle('disabled', soldOut);
              label?.classList.remove('rf-color-absent');
              label?.querySelector('.rf-color-absent__note')?.remove();
              label?.classList.toggle('rf-color-sold-out', soldOut);
              let note = label?.querySelector('.rf-color-sold-out__note');
              if (soldOut && !note) {
                note = document.createElement('span');
                note.className = 'rf-color-sold-out__note';
                note.textContent = 'Out of stock';
                label?.append(note);
              } else if (!soldOut) note?.remove();
            }
            if (group.dataset.rfDimension) {
              const unavailable = secondsFilter && !!input.nextElementSibling?.matches('.rf-dimension-option--sold-out, .rf-dimension-option--not-offered, .rf-width-absent');
              input.nextElementSibling?.classList.toggle('rf-seconds-unavailable', unavailable);
              input.hidden = unavailable;
              if (unavailable) input.disabled = true;
            }
          });
        });
        if (this.variantSelectors.dataset.rfTaffyQuarterComingSoon === 'true') {
          const widthGroup = groups.find((group) => group.dataset.rfDimension === 'width');
          const quarter = Array.from(widthGroup?.querySelectorAll('input[type="radio"]') || [])
            .find((input) => input.value === '1/4"');
          const label = quarter?.nextElementSibling;
          if (quarter && label) {
            quarter.dataset.rfComingSoon = 'true';
            if (!quarter.checked) quarter.disabled = true;
            label.querySelectorAll('.rf-dimension-option__status, .rf-ordered-stock').forEach((note) => note.remove());
            if (!label.querySelector('.rf-coming-soon-note')) {
              const note = document.createElement('span');
              note.className = 'rf-dimension-option__status rf-coming-soon-note';
              note.textContent = 'Coming soon';
              label.append(note);
            }
          }
        }
        // A final-choice price belongs only to this exact confirmed prefix.
        const prefixReady = groups.slice(0, -1).every((group, index) => this.rfChosenOptions.has(index));
        const last = groups.length - 1;
        groups[last]?.querySelectorAll('input[type="radio"]').forEach((input) => {
          const label = input.nextElementSibling;
          const price = label?.querySelector('.rf-option-price');
          if (!price) return;
          const options = JSON.parse(price.dataset.rfPriceOptions || '[]');
          const exact = options.length === groups.length && options[last] === input.value
            && groups.slice(0, -1).every((group, index) => options[index] === group.querySelector('input:checked')?.value);
          const show = prefixReady && exact && this.dataset.rfChoicesBusy !== 'true';
          price.hidden = !show;
          label.classList.toggle('rf-priced-option', show);
        });
        this.rfSyncWidthPhotoPreviews(groups);
        this.rfUpdateChoiceSummary(groups, next);
        this.rfSyncPullCodePromos(groups, complete);
        groups.forEach(group => this.rfEqualizeDimensionCards(group));
        const caption = this.variantSelectors.querySelector('.rf-color-match-context');
        const dimensions = groups.filter((group) => group.dataset.rfDimension).map((group) => group.querySelector('input:checked')?.value).filter(Boolean);
        if (caption && dimensions.length) {
          const text = 'Colors for ' + dimensions.join(' × ');
          if (caption.textContent !== text) caption.textContent = text;
        }
        let prompt = this.variantSelectors.querySelector('.rf-order-prompt');
        if (!prompt) {
          prompt = document.createElement('p');
          prompt.className = 'rf-order-prompt';
          prompt.setAttribute('role', 'status');
          this.variantSelectors.prepend(prompt);
        }
        const name = next < 0 ? '' : (groups[next].dataset.rfOptionName || groups[next].querySelector('legend')?.textContent || 'option').trim();
        const chosen = groups.filter((group, index) => index !== next && this.rfChosenOptions.has(index)).map((group) => {const input = group.querySelector('input:checked'); return input?.dataset.rfChoiceLabel || input?.value;}).filter(Boolean);
        const pullStart = this.rfPullOpen() && next === 1 && this.rfChosenOptions.size === 1;
        const widthStarterStart = this.variantSelectors.dataset.rfWidthStarter === 'true' && this.rfChosenOptions.size === 0;
        const starterPrompt = !this.rfWidthStarterMode ? 'Choose a color or width to get started.' : this.rfWidthStarterMode === 'width' ? 'Choose a width to continue.' : 'Choose a color to continue.';
        const text = complete ? '' : pullStart ? 'Choose a tensile strength to get started.' : widthStarterStart ? starterPrompt : 'Choose ' + (this.rfPullOpen() ? 'your ' : '') + name.toLowerCase() + (chosen.length ? ' for ' + chosen.join(' · ') : ' to get started') + '.';
        const tracerHint = pullStart
          && !/detectable/i.test(groups[0]?.querySelector('input:checked')?.value || '')
          && Array.from(groups[0]?.querySelectorAll('input') || []).some(input => /detectable/i.test(input.value))
          ? 'For tracer wire, select Detectable.' : '';
        const promptText = text + (tracerHint ? ' ' + tracerHint : '');
        if (prompt.textContent !== promptText) {
          prompt.textContent = text;
          if (tracerHint) {
            const hint = document.createElement('span');
            hint.dataset.rfTracerHint = 'true';
            hint.textContent = tracerHint;
            prompt.append(' ', hint);
          }
        }
        prompt.hidden = complete;
        if (!complete || this.dataset.rfChoicesBusy === 'true') {
          const widthStarterPending = this.variantSelectors.dataset.rfWidthStarter === 'true'
            && this.variantSelectors.dataset.rfWidthStarterMode === 'width'
            && this.variantSelectors.dataset.rfWidthStarterPicked === 'false';
          const label = this.dataset.rfChoicesBusy === 'true' ? 'Updating options…' : widthStarterPending ? 'Choose width' : 'Choose ' + name.toLowerCase();
          const button = this.querySelector('[id^="ProductSubmitButton-"]');
          if (button) {
            if (!button.disabled) button.disabled = true;
            const span = button.querySelector(':scope > span:not(.loading__spinner)');
            if (span && span.textContent.trim() !== label) span.textContent = label;
          }
          this.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            input.value = '';
            input.disabled = true;
          });
        }
        if (["/products/taffy-stripe-ribbon","/products/1-1-2-monarch-stripe-ribbon","/products/solid-colors","/products/double-face-satin","/products/seconds-bi-stripe-100-yd"].includes(this.dataset.url)) {
          this.dispatchEvent(new CustomEvent('rf:stripe-choices-updated', { bubbles: true }));
        }
        this.rfQueueVariantLanding();
      }

      rfEqualizeDimensionCards(group) {
        if (!group.isConnected || getComputedStyle(group).display === 'none') return;
        group.removeAttribute('data-rf-uniform-cards');
        group.style.removeProperty('--rf-choice-card-height');
        const labels = Array.from(group.querySelectorAll('input[type="radio"]')).map(input => input.nextElementSibling)
          .filter(label => label?.tagName === 'LABEL' && label.getBoundingClientRect().height > 0 && getComputedStyle(label).display !== 'none');
        if (!labels.length) return;
        const height = Math.ceil(Math.max(...labels.map(label => {
          const style = getComputedStyle(label);
          const border = parseFloat(style.borderTopWidth || 0) + parseFloat(style.borderBottomWidth || 0);
          const padding = parseFloat(style.paddingTop || 0) + parseFloat(style.paddingBottom || 0);
          const content = [];
          const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
          while (walker.nextNode()) {
            const node = walker.currentNode;
            if (!node.textContent.trim() || node.parentElement.closest('.visually-hidden,[hidden]')) continue;
            const range = document.createRange();
            range.selectNodeContents(node);
            content.push(...Array.from(range.getClientRects()).filter(rect => rect.width > 0 && rect.height > 0));
          }
          label.querySelectorAll('img').forEach(image => {
            const rect = image.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) content.push(rect);
          });
          const intrinsic = content.length ? Math.max(...content.map(rect => rect.bottom)) - Math.min(...content.map(rect => rect.top)) + padding + border : 0;
          return Math.max(label.getBoundingClientRect().height, label.scrollHeight + border, intrinsic);
        })));
        if (height > 0) {
          group.style.setProperty('--rf-choice-card-height', height + 'px');
          group.dataset.rfUniformCards = 'true';
        }
      }

      rfChooseWidthFirst(value) {
        if (!this.rfOrderedEnabled() || this.dataset.rfChoicesBusy === 'true') return false;
        const groups = this.rfOrderedGroups();
        const index = groups.findIndex((group) => group.dataset.rfDimension === 'width');
        const input = Array.from(groups[index]?.querySelectorAll('input[type="radio"]') || [])
          .find((item) => item.value === value);
        if (!input) return false;
        this.rfChosenOptions ||= this.rfInitialChoices();
        this.rfChosenOptions.clear();
        this.rfLinkedChoices = new Set([index]);
        this.rfWidthStarterChoosing = true;
        try { this.rfHandleOrderedChange(null, input); }
        finally { this.rfWidthStarterChoosing = false; }
        return true;
      }

      rfHandleOrderedChange(event, target) {
        this.rfLinkedEditIndex = undefined;
        const groups = this.rfOrderedGroups();
        const index = groups.findIndex((group) => group.contains(target));
        if (index < 0) return;
        delete this.dataset.rfChoiceScrollTarget;
        this.rfChosenOptions ||= new Map();
        const next = groups.findIndex((group, item) => !this.rfChosenOptions.has(item));
        if (!this.rfPullOpen() && next >= 0 && index > next && !this.rfWidthStarterChoosing) return;
        const gray = !this.rfWidthStarterChoosing && (target.dataset.rfVariantState === 'not-offered' || target.nextElementSibling?.matches('.rf-width-absent, .rf-dimension-option--not-offered'));
        const explore = gray && index > 0;
        if (this.rfTwillVariants()) {
          // A new twill weight always starts its own Width → Length path.
          // A gray width may have brought the shopper here, but it is not a
          // confirmed width choice for the newly selected weight.
          if (index === 0) {
            for (let n = 1; n < groups.length; n++) {
              this.rfChosenOptions.delete(n);
              this.rfLinkedChoices?.delete(n);
            }
          }
          let matches = this.rfTwillVariants().filter(variant => variant.options[index] === target.value);
          // The latest requested dimension wins; keep only compatible choices.
          for (let n = 0; n < groups.length; n++) {
            if (n === index || !this.rfChosenOptions.has(n)) continue;
            const value = Array.from(groups[n].querySelectorAll('input')).find(input => input.dataset.optionValueId === this.rfChosenOptions.get(n))?.value;
            const compatible = matches.filter(variant => variant.options[n] === value);
            if (compatible.length && (n < index || index === 0 && n === 1 && this.rfLinkedChoices?.has(n))) matches = compatible;
            else this.rfChosenOptions.delete(n);
          }
          if (gray && index > 0) this.rfLinkedChoices = new Set([index]);
        } else if (this.rfPullOpen()) {
          this.rfChosenOptions.set(index, target.dataset.optionValueId);
          let matches = this.rfPullVariants().filter((variant) => variant.options[index] === target.value);
          // Keep compatible choices, prioritizing the customer's latest click.
          // An unusual length must not silently retain an impossible strength.
          for (let n = 0; n < groups.length; n++) {
            if (n === index || !this.rfChosenOptions.has(n)) continue;
            const value = Array.from(groups[n].querySelectorAll('input')).find((input) => input.dataset.optionValueId === this.rfChosenOptions.get(n))?.value;
            const compatible = matches.filter((variant) => variant.options[n] === value);
            if (compatible.length) matches = compatible;
            else this.rfChosenOptions.delete(n);
          }
          this.rfLinkedChoices = new Set(this.rfChosenOptions.keys());
        } else if (explore) {
          this.rfChosenOptions.clear();
          this.rfLinkedChoices = new Set([index]);
        } else if (gray && !groups[index].dataset.rfDimension) {
          this.rfLinkedChoices?.clear();
        }
        if (groups[index].dataset.rfOptionName?.toLowerCase() === 'color' && window.matchMedia('(max-width: 749px)').matches) {
          window.rfColorBrowseAll = false;
        }
        const changingExisting = !explore && this.rfChosenOptions.has(index);
        this.rfChosenOptions.set(index, target.dataset.optionValueId);
        for (let item = index + 1; !this.rfPullOpen() && !this.rfTwillVariants() && item < groups.length; item++) {
          if (changingExisting || !this.rfLinkedChoices?.has(item)) {
            this.rfChosenOptions.delete(item);
            this.rfLinkedChoices?.delete(item);
          }
        }
        this.dataset.rfChoicesBusy = 'true';
        this.rfApplyChoices();
        const nextGroup = groups.find((group, item) => !this.rfChosenOptions.has(item));
        if (nextGroup && this.dataset.url?.includes('awareness-ribbons') && window.matchMedia('(max-width: 749px)').matches) {
          this.rfScrollChoice(nextGroup, true);
        }
        const values = groups.map((group, item) => this.rfChosenOptions.get(item)).filter(Boolean);
        const productUrl = this.dataset.url;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, values),
          targetId: target.id,
          callback: async (html) => {
            const choiceRequest = this.abortController;
            const complete = this.rfChosenOptions.size === groups.length;
            delete this.dataset.rfChoicesBusy;
            if (complete) {
              this.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => { input.disabled = false; });
              // Explicit choices identify the exact variant. Do not silently
              // substitute a different color, width, or length when sold out.
              await this.handleUpdateProductInfo(productUrl, index, true)(html);
            } else {
              const variant = this.getSelectedVariant(html);
              this.updateOptionValues(html);
              this.updateMedia(html, variant?.featured_media?.id || html.querySelector('media-gallery')?.dataset.rfPreviewMedia);
              this.updateURL(productUrl, null);
              this.pickupAvailability?.update(null);
            }
            this.rfApplyChoices();
            this.dispatchEvent(new CustomEvent('rf:choices-updated', { bubbles: true }));
            const current = this.rfOrderedGroups();
            const nextIndex = current.findIndex((group, item) => !this.rfChosenOptions.has(item));
            const landOnSecondsColor = this.dataset.rfSecondsColorLanding === target.dataset.optionValueId;
            delete this.dataset.rfSecondsColorLanding;
            const colorGroup = current.find((group) => group.dataset.rfOptionName?.trim().toLowerCase() === 'color');
            const anchor = landOnSecondsColor && colorGroup ? colorGroup : current[nextIndex >= 0 ? nextIndex : index];
            requestAnimationFrame(() => requestAnimationFrame(() => {
              if (!this.isConnected || choiceRequest !== this.abortController || choiceRequest?.signal.aborted || this.dataset.rfChoicesBusy === 'true') return;
              // One owner advances every screen size to the next required choice.
              // Once choices are complete, the purchase row is the destination;
              // an offer above the completed Length field must not pull it back up.
              const purchase = nextIndex < 0 ? Array.from(this.querySelectorAll('.rf-price-qty-row, .rf-pull-price-qty, .rf-twill-price-qty, .rf-buy-focus > .rf-price-sample-row'))
                .find(row => row.getBoundingClientRect().height > 0) || this.querySelector('[id^="ProductSubmitButton-"]') : null;
              const destination = purchase || anchor;
              if (!destination?.isConnected) return;
              this.rfScrollChoice(destination, true, false, Boolean(purchase));
              this.dataset.rfChoiceScrollTarget = purchase ? 'purchase' : anchor.dataset.rfOptionName || 'options';
            }));
          },
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (this.rfOrderedEnabled()) {
          this.rfHandleOrderedChange(event, target);
          return;
        }
        this.rfLengthObserver?.disconnect();
        this.rfLengthObserver = null;
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const changedOptionIndex = Array.from(this.variantSelectors?.querySelectorAll('fieldset, select') || []).findIndex(
          (group) => group === target || group.contains(target)
        );
        const isAutomaticOptionChange = this.dataset.autoVariantNextId === target.id;
        delete this.dataset.autoVariantNextId;
        if (!isAutomaticOptionChange) {
          delete this.dataset.autoVariantAttempts;
          delete this.dataset.rfSoldOutFallback;
          this.dataset.autoVariantAnchorIndex = String(changedOptionIndex);
          const label = target.nextElementSibling;
          const isOtherSelectionNeeded = target.dataset.rfVariantState === 'not-offered'
            || label?.classList.contains('rf-width-absent')
            || label?.classList.contains('rf-color-absent');
          if (isOtherSelectionNeeded) this.dataset.rfResolveOtherOptions = 'true';
          else delete this.dataset.rfResolveOtherOptions;
        }
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, changedOptionIndex, target.dataset.rfVariantState === 'sold-out'),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              if (this.rfOrderedEnabled()) {
                delete this.dataset.rfChoicesBusy;
                const failed = this.rfOrderedGroups().findIndex((group) => group.querySelector('[id="' + targetId + '"]'));
                if (failed >= 0) this.rfChosenOptions?.delete(failed);
                this.rfApplyChoices();
              }
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          this.preProcessHtmlCallbacks.forEach((callback) => callback(variantSelects));
          this.variantSelectors.replaceWith(variantSelects);
        }
      }

      handleUpdateProductInfo(productUrl, changedOptionIndex, preserveSoldOut = false) {
        return async (html) => {
          const variant = this.getSelectedVariant(html);

          const changedGroup = Array.from(this.variantSelectors?.querySelectorAll('fieldset, select') || [])[changedOptionIndex];
          const widthChanged = changedGroup?.dataset.rfDimension === 'width';
          const colorChanged = (changedGroup?.dataset.rfOptionName || changedGroup?.querySelector('legend')?.textContent || '').trim().toLowerCase() === 'color';
          const resolveOtherOptions = this.dataset.rfResolveOtherOptions === 'true';
          const requestedOptionIndex = Number(this.dataset.autoVariantAnchorIndex ?? changedOptionIndex);
          const preferredLength = !preserveSoldOut && ((widthChanged && requestedOptionIndex === changedOptionIndex)
            || (colorChanged && !variant?.available && requestedOptionIndex === changedOptionIndex))
            ? getPreferredLengthOption(html, resolveOtherOptions) : null;
          const selectedLength = html.querySelector('variant-selects fieldset[data-rf-dimension="length"] input[type="radio"]:checked');
          if (preferredLength && (preferredLength.id !== selectedLength?.id || !variant)) {
            queuePreferredLengthSelection(this, preferredLength.id);
            this.updateOptionValues(html);
            return;
          }

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          if (!preserveSoldOut && (resolveOtherOptions || (!widthChanged && !colorChanged))
            && await rfResolveVariantCombination(this, variant, changedOptionIndex)) return;
          if (this.abortController?.signal.aborted) return;
          delete this.dataset.rfResolveOtherOptions;
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const variantQuery = variantId ? `?variant=${variantId}` : '';
        const secondsQuery = window.rfSecondsEntry ? `${variantId ? '&' : '?'}rf_seconds=1` : '';
        const destination = `${url}${variantQuery}${secondsQuery}`;
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${destination}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', destination);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        variantFeaturedMediaId = this.rfWidthPreviewPhoto()?.mediaId || variantFeaturedMediaId;
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { scrollPage: !this.rfOrderedEnabled() }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


function getPreferredLengthOption(html, availableOnly = false) {
  const lengths = Array.from(html.querySelectorAll('variant-selects fieldset[data-rf-dimension="length"] input[type="radio"]'))
    .sort((a, b) => Number.parseFloat(a.value.replace(/,/g, '')) - Number.parseFloat(b.value.replace(/,/g, '')));
  return lengths.find((input) => input.dataset.rfVariantState === 'available' && !input.disabled)
    || (availableOnly ? null : lengths.find((input) => input.dataset.rfVariantState === 'sold-out'));
}

function queuePreferredLengthSelection(productInfo, preferredId) {
  productInfo.rfLengthObserver?.disconnect();
  const priorInput = Array.from(productInfo.querySelectorAll('variant-selects fieldset[data-rf-dimension="length"] input[type="radio"]'))
    .find((input) => input.id === preferredId);
  const observer = new MutationObserver(() => {
    const updated = Array.from(productInfo.querySelectorAll('variant-selects fieldset[data-rf-dimension="length"] input[type="radio"]'))
      .find((input) => input.id === preferredId);
    if (!updated || updated === priorInput) return;
    observer.disconnect();
    productInfo.rfLengthObserver = null;
    updated.checked = true;
    productInfo.dataset.autoVariantNextId = updated.id;
    updated.dispatchEvent(new Event('change', { bubbles: true }));
  });
  productInfo.rfLengthObserver = observer;
  observer.observe(productInfo, { childList: true, subtree: true });
  setTimeout(() => {
    if (productInfo.rfLengthObserver !== observer) return;
    observer.disconnect();
    productInfo.rfLengthObserver = null;
    productInfo.setUnavailable();
  }, 2500);
}

const rfProductVariantCache = new Map();

async function rfResolveVariantCombination(productInfo, variant, changedOptionIndex) {
  const clearResolution = () => {
    delete productInfo.dataset.autoVariantAttempts;
    delete productInfo.dataset.autoVariantAnchorIndex;
    delete productInfo.dataset.rfResolveOtherOptions;
    delete productInfo.dataset.rfSoldOutFallback;
  };
  if (variant?.available) {
    clearResolution();
    return false;
  }
  const groups = Array.from(productInfo.querySelectorAll('variant-selects fieldset, variant-selects select'));
  const anchorIndex = Number(productInfo.dataset.autoVariantAnchorIndex ?? changedOptionIndex);
  if (!groups.length || anchorIndex < 0 || anchorIndex >= groups.length) {
    clearResolution();
    return false;
  }
  const controls = groups.map((group) => group.matches('select')
    ? Array.from(group.options) : Array.from(group.querySelectorAll('input')));
  const selected = controls.map((options) => options.find((option) => option.checked || option.selected)?.value);
  const anchorValue = selected[anchorIndex];
  if (!anchorValue) {
    clearResolution();
    return false;
  }

  // The rendered option states only describe the current combination. A gray
  // choice can require two other options to change together.
  const productUrl = `${productInfo.dataset.url.replace(/\/$/, '')}.js`;
  if (!rfProductVariantCache.has(productUrl)) {
    rfProductVariantCache.set(productUrl, fetch(productUrl)
      .then((response) => {
        if (!response.ok) throw new Error('Product variants unavailable');
        return response.json();
      }).catch((error) => {
        rfProductVariantCache.delete(productUrl);
        throw error;
      }));
  }
  let product;
  try {
    product = await rfProductVariantCache.get(productUrl);
  } catch (error) {
    console.error(error);
    clearResolution();
    return false;
  }
  if (productInfo.abortController?.signal.aborted || !productInfo.isConnected) return true;

  const matches = (product.variants || []).filter((candidate) =>
    candidate.options?.[anchorIndex] === anchorValue && candidate.options.length === groups.length);
  const score = (candidate) => candidate.options.reduce((count, value, index) =>
    count + (index !== anchorIndex && value === selected[index] ? 1 : 0), 0);
  matches.sort((a, b) => a.available === b.available ? score(b) - score(a) : a.available ? -1 : 1);
  const best = matches[0];
  if (!best) {
    // The public product JSON can stop at 250 variants. Retain the rendered
    // option fallback for products whose remaining variants are outside it.
    return selectFirstAvailableVariantOption(productInfo, variant, changedOptionIndex);
  }
  const changes = best.options.map((value, index) => ({
    index,
    group: groups[index],
    control: controls[index].find((control) => control.value === value)
  })).filter(({ index, control }) => index !== anchorIndex && control && selected[index] !== control.value);
  if (!changes.length) {
    clearResolution();
    return false;
  }

  changes.forEach(({ group, control }) => {
    if (group.matches('select')) {
      group.value = control.value;
      group.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
      control.setAttribute('selected', 'selected');
    } else {
      control.checked = true;
    }
  });
  const trigger = changes.find(({ group }) => group.dataset.rfDimension === 'length') || changes.at(-1);
  productInfo.dataset.autoVariantNextId = trigger.control.id;
  trigger.control.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

function selectFirstAvailableVariantOption(productInfo, variant, changedOptionIndex = -1) {
  if (variant?.available) {
    delete productInfo.dataset.autoVariantAttempts;
    delete productInfo.dataset.autoVariantAnchorIndex;
    delete productInfo.dataset.rfResolveOtherOptions;
    return false;
  }

  const variantSelectors = productInfo.querySelector('variant-selects');
  const groups = Array.from(variantSelectors?.querySelectorAll('fieldset, select') || []);
  const attempts = Number(productInfo.dataset.autoVariantAttempts || 0);
  const anchorIndex = Number(productInfo.dataset.autoVariantAnchorIndex ?? changedOptionIndex);

  if (productInfo.dataset.rfSoldOutFallback === 'true' && variant) {
    delete productInfo.dataset.rfSoldOutFallback;
    delete productInfo.dataset.autoVariantAttempts;
    delete productInfo.dataset.autoVariantAnchorIndex;
    delete productInfo.dataset.rfResolveOtherOptions;
    return false;
  }

  if (!groups.length || attempts >= groups.length * 2) {
    delete productInfo.dataset.autoVariantAttempts;
    delete productInfo.dataset.autoVariantAnchorIndex;
    delete productInfo.dataset.rfResolveOtherOptions;
    return false;
  }

  // Keep the option the customer just chose. Find a purchasable combination
  // by changing another option, starting with the last option.
  for (let index = groups.length - 1; index >= 0; index -= 1) {
    if (index === anchorIndex) continue;

    const group = groups[index];
    const controls = group.matches('select') ? Array.from(group.options) : Array.from(group.querySelectorAll('input'));
    const isSelected = (control) => control.selected || control.checked;
    const isAvailable = (control) => {
      if (control.disabled || control.classList.contains('disabled') || control.getAttribute('aria-disabled') === 'true') return false;
      if (control.dataset.rfVariantState === 'not-offered' || control.dataset.rfVariantState === 'sold-out') return false;
      if ((group.dataset.rfOptionName || group.querySelector('legend')?.textContent || '').trim().toLowerCase() === 'color'
        && control.dataset.rfCombinationExists === 'false') return false;
      if (group.dataset.rfDimension === 'width' && index !== anchorIndex) {
        return control.dataset.rfSubtreeAvailable === 'true';
      }
      return true;
    };

    let candidate = controls.find((control) => !isSelected(control) && isAvailable(control));

    // Exact combinations can all be unavailable after a Color change. A middle
    // option may still have stock at a different value of the final option.
    if (!candidate && anchorIndex === 0 && index > 0 && index < groups.length - 1) {
      candidate = controls.find(
        (control) => !isSelected(control) && control.dataset.rfSubtreeAvailable === 'true'
      );
    }

    if (!candidate) continue;

    productInfo.dataset.autoVariantAttempts = String(attempts + 1);
    productInfo.dataset.autoVariantNextId = candidate.id;
    if (group.matches('select')) {
      group.value = candidate.value;
      group.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      candidate.click();
    }
    return true;
  }

  // If the chosen style has no in-stock combination, select a real sold-out
  // variant so its photo and Sold out state replace the previous style.
  for (let index = groups.length - 1; index >= 0; index -= 1) {
    if (index === anchorIndex) continue;

    const group = groups[index];
    const controls = group.matches('select') ? Array.from(group.options) : Array.from(group.querySelectorAll('input'));
    const candidate = controls.find((control) => {
      if (control.selected || control.checked) return false;
      if (group.dataset.rfDimension === 'length') return control.dataset.rfVariantState === 'sold-out';
      if (group.dataset.rfDimension === 'width') return control.dataset.rfExactExists === 'true';
      return false;
    });
    if (!candidate) continue;

    productInfo.dataset.autoVariantAttempts = String(attempts + 1);
    productInfo.dataset.autoVariantNextId = candidate.id;
    productInfo.dataset.rfSoldOutFallback = 'true';
    if (group.matches('select')) {
      group.value = candidate.value;
      group.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      candidate.click();
    }
    return true;
  }

  delete productInfo.dataset.autoVariantAttempts;
  delete productInfo.dataset.autoVariantAnchorIndex;
  delete productInfo.dataset.rfResolveOtherOptions;
  return false;
}
